/* Search popup with predictive results (Shopify /search/suggest.json).
   The header icon opens it; typing shows suggestions, products and collections/pages;
   only "Search for …" (or Enter) goes to the full search page. */
(() => {
  const modal = document.getElementById('PsSearchModal');
  if (!modal || typeof modal.showModal !== 'function') return; // no <dialog> support: icon stays a /search link

  const input = modal.querySelector('#PsSearchInput');
  const results = modal.querySelector('#PsSearchResults');
  const d = results.dataset;
  const searchUrl = d.searchUrl || '/search';
  const suggestUrl = (d.suggestUrl || '/search/suggest') + '.json';
  const popular = (d.popular || '').split(',').map((s) => s.trim()).filter(Boolean);

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: n % 1 ? 2 : 0 })}` : '';
  };
  const sized = (src, w) => (src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '');
  // result types come from Theme settings > Search (popup may include collections; the search page can't)
  const types = d.types || 'product';
  const pageTypes = d.pageTypes || 'product';
  const fullSearch = (q) => `${searchUrl}?q=${encodeURIComponent(q)}&type=${encodeURIComponent(pageTypes)}&options%5Bprefix%5D=last`;

  /* ---------- open / close ---------- */
  const open = () => {
    if (modal.open) return;
    modal.showModal();
    document.documentElement.classList.add('ps-search-open');
    renderIdle();
    requestAnimationFrame(() => { input.focus(); input.select(); });
  };
  const close = () => { if (modal.open) modal.close(); };
  modal.addEventListener('close', () => document.documentElement.classList.remove('ps-search-open'));

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-ps-search-open]');
    if (opener) { e.preventDefault(); open(); return; }
    if (e.target.closest('[data-ps-search-close]')) close();
  });
  // Escape always closes (in a search box Chrome's first Escape would only clear the text)
  modal.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.preventDefault(); close(); } });
  // click on the dimmed backdrop (outside the panel) closes
  modal.addEventListener('click', (e) => { if (e.target === modal) close(); });

  /* ---------- rendering ---------- */
  function renderIdle() {
    if (input.value.trim()) return;
    results.innerHTML = popular.length
      ? `<div class="ps-search-modal__group"><p class="ps-search-modal__heading">${esc(d.popularLabel)}</p>
           <div class="ps-search-modal__chips">${popular.map((t) => `<a class="ps-search-modal__chip" href="${fullSearch(t)}" data-term="${esc(t)}">${esc(t)}</a>`).join('')}</div></div>`
      : '';
  }

  function render(q, r) {
    const products = (r.products || []).slice(0, 6);
    const queries = (r.queries || []).filter((x) => x.text.toLowerCase() !== q.toLowerCase()).slice(0, 4);
    const links = [...(r.collections || []).slice(0, 3), ...(r.pages || []).slice(0, 3), ...(r.articles || []).slice(0, 3)];
    let html = '';
    if (!products.length && !queries.length && !links.length) {
      html += `<p class="ps-search-modal__empty">${esc(d.emptyLabel)} “${esc(q)}”</p>`;
    } else {
      html += '<div class="ps-search-modal__cols">';
      if (queries.length || links.length) {
        html += '<div class="ps-search-modal__side">';
        if (queries.length) {
          html += `<div class="ps-search-modal__group"><p class="ps-search-modal__heading">${esc(d.suggestionsLabel)}</p><ul class="ps-search-modal__list">${
            queries.map((x) => `<li><a href="${fullSearch(x.text)}" data-term="${esc(x.text)}">${x.styled_text || esc(x.text)}</a></li>`).join('')}</ul></div>`;
        }
        if (links.length) {
          html += `<div class="ps-search-modal__group"><p class="ps-search-modal__heading">${esc(d.pagesLabel)}</p><ul class="ps-search-modal__list">${
            links.map((x) => `<li><a href="${esc(x.url)}">${esc(x.title)}</a></li>`).join('')}</ul></div>`;
        }
        html += '</div>';
      }
      if (products.length) {
        html += `<div class="ps-search-modal__group ps-search-modal__products-wrap"><p class="ps-search-modal__heading">${esc(d.productsLabel)}</p><ul class="ps-search-modal__products">${
          products.map((p) => {
            const img = p.featured_image?.url || p.image;
            const w = p.featured_image?.width || 400; const h = p.featured_image?.height || 400;
            return `<li><a class="ps-search-modal__product" href="${esc(p.url)}">
              <span class="ps-search-modal__thumb">${img ? `<img src="${esc(sized(img, 200))}" srcset="${esc(sized(img, 200))} 200w, ${esc(sized(img, 400))} 400w" sizes="(max-width: 749px) 30vw, 140px" width="${w}" height="${h}" alt="${esc(p.featured_image?.alt || p.title)}" loading="lazy">` : ''}</span>
              <span class="ps-search-modal__ptitle">${esc(p.title)}</span>
              <span class="ps-search-modal__price">${money(p.price)}</span></a></li>`;
          }).join('')}</ul></div>`;
      }
      html += '</div>';
    }
    html += `<a class="ps-search-modal__all" href="${fullSearch(q)}">${esc(d.allLabel)} “${esc(q)}” <span aria-hidden="true">→</span></a>`;
    results.innerHTML = html;
  }

  /* ---------- fetching ---------- */
  let timer; let ctrl; const cache = new Map();
  async function suggest(q) {
    if (cache.has(q)) { render(q, cache.get(q)); return; }
    ctrl?.abort(); ctrl = new AbortController();
    results.classList.add('is-loading');
    try {
      const params = new URLSearchParams({ q, 'resources[type]': `${types},query`, 'resources[limit]': '6', 'resources[options][unavailable_products]': 'last' });
      const res = await fetch(`${suggestUrl}?${params}`, { signal: ctrl.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(res.status);
      const data = (await res.json()).resources?.results || {};
      cache.set(q, data);
      if (input.value.trim() === q) render(q, data);
    } catch (err) {
      if (err.name !== 'AbortError' && input.value.trim() === q) render(q, {});
    } finally { results.classList.remove('is-loading'); }
  }

  input.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (!q) { ctrl?.abort(); renderIdle(); return; }
    timer = setTimeout(() => suggest(q), 220);
  });

  // popular / suggestion chips: fill the box and show results in the popup instead of leaving the page
  results.addEventListener('click', (e) => {
    const t = e.target.closest('[data-term]');
    if (!t || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    input.value = t.dataset.term;
    input.focus();
    suggest(input.value.trim());
  });
})();
